import React, { useEffect, useRef, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { IMAGE_LOAD_RETRIES } from '../../../../enums/Constraints';

const useStyles = makeStyles(() => ({
  cover: {
    backgroundPosition: '50%',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    objectFit: 'cover',
  },
  container: {
    height: '100%',
    pointerEvents: 'none',
    width: '100%',
  },
  skeleton: {
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 1,
  },
}));

interface Props {
  src?: string;
}

const RoomContentTileCover: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();

  const { src } = props;

  const [imgReady, setImgReady] = useState(false);
  const [retries, setRetries] = useState(0);
  const [lastRetry, setLastRetry] = useState(0);

  const imgRef = useRef(new Image());

  useEffect(() => {
    if (!src) {
      return;
    }

    let timeout: ReturnType<typeof setTimeout>;

    imgRef.current.onload = () => {
      setImgReady(true);
    };

    imgRef.current.onerror = () => {
      if (retries >= IMAGE_LOAD_RETRIES) {
        return;
      }

      setRetries((retries) => retries + 1);
      timeout = setTimeout(() => {
        setLastRetry(Date.now());
      }, 1000 + 1000 * retries);
    };

    imgRef.current.src = src;

    return () => {
      clearTimeout(timeout);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRetry, src]);

  if (!src) {
    return (
      <Box
        className={classes.container}
        style={{ backgroundColor: 'var(--cover-background-color)' }}
      />
    );
  }

  return (
    <Box className={classes.container} sx={{ position: 'absolute' }}>
      {!imgReady && retries > 0 && (
        <Box className={classes.skeleton}>
          <Skeleton height="100%" variant="rectangular" width="100%" />
        </Box>
      )}
      <Box
        className={classNames(classes.container, classes.cover)}
        sx={{ backgroundImage: imgReady ? `url(${src})` : undefined }}
      />
    </Box>
  );
};

export default RoomContentTileCover;
